import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Icon,
  Button,
  IconButton,
  Image,
  CircularProgress,
  CircularProgressLabel,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  FaExclamationTriangle,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaPencilAlt,
  FaPlus,
} from "react-icons/fa";
import { formatDuration } from "../../../_utils/common";
import * as PostMediaTypes from "./PostMediaTypes.constants";
import { useTranslation } from "react-i18next";

const wrapperStyles = ({ size, hasError }) => {
  const widthHeight = size === "sm" ? "64px" : "128px";
  const borderRadius = "base";
  const styles = {
    position: "relative",
    width: widthHeight,
    height: widthHeight,
    borderRadius,
    backgroundColor: "whiteAlpha.200",
  };

  if (hasError) {
    styles._before = {
      position: "absolute",
      inset: "-1px",
      content: '""',
      border: "2px solid",
      borderColor: "brand.negative",
      borderRadius,
      pointerEvents: "none",
    };
  }

  return styles;
};

const innerStyles = {
  width: "100%",
  height: "100%",
  overflow: "hidden",
  borderRadius: "base",
};

const errorStyles = {
  position: "absolute",
  left: "1",
  bottom: "1",
  color: "brand.negative",
  lineHeight: "1",
};

const timeStyles = {
  position: "absolute",
  left: "1",
  top: "1",
  padding: "1",
  backgroundColor: "blackAlpha.500",
  borderRadius: "full",
  color: "white",
  fontSize: "12px",
  fontWeight: "400",
  lineHeight: "1",
};

const progressStyles = {
  position: "absolute",
  inset: "0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
};

const toolbarButtonStyles = {
  position: "relative",
  zIndex: "0",
  overflow: "hidden",
  borderRadius: "full",
  _before: {
    position: "absolute",
    inset: "0",
    zIndex: "-1",
    content: '""',
    background: "brand.grey-darker",
  },
};

const PostMediaListItem = ({
  errors,
  onClick,
  onAdd,
  onEditThumb,
  onRemove,
  onMove,
  clickTooltip,
  removeTooltip,
  isFirst,
  isLast,
  size,
  ...item
}) => {
  const { type, src, progress, duration } = item;
  const { t } = useTranslation(["planner"]);
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const [hover, setHover] = useState(false);
  const inProgress = useMemo(() => progress > 0 || progress === 0, [progress]);
  const progressDescription = useMemo(() => {
    if (progress === 0) {
      return t("post_media_list.progress.prepare");
    } else if (progress === 100) {
      return t("post_media_list.progress.finish");
    } else if (progress > 0) {
      return t("post_media_list.progress.uploading", {
        percent: progress,
      });
    }
  }, [progress, t]);

  const handleClick = useCallback(() => {
    onClick?.(item);
  }, [onClick, item]);

  const handleAdd = useCallback(() => {
    onAdd?.(item);
  }, [onAdd, item]);

  const handleEditThumb = useCallback(() => {
    onEditThumb?.(item);
  }, [onEditThumb, item]);

  const handleRemove = useCallback(() => {
    onRemove?.(item);
  }, [onRemove, item]);

  const handleMoveLeft = useCallback(() => {
    onMove?.(item, -1);
  }, [onMove, item]);

  const handleMoveRight = useCallback(() => {
    onMove?.(item, 1);
  }, [onMove, item]);

  const handleMouseEnter = useCallback(() => setHover(true), [setHover]);

  const handleMouseLeave = useCallback(() => setHover(false), [setHover]);

  const toolbarButtonSize = size === "sm" ? "xs" : "sm";

  return (
    <Box
      sx={wrapperStyles({ size, hasError: !!errors?.length })}
      onMouseEnter={handleMouseEnter}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box sx={innerStyles} title={inProgress ? progressDescription : null}>
        <Tooltip
          label={clickTooltip}
          hasArrow
          placement="top"
          isDisabled={!onClick || !clickTooltip}
        >
          <Box
            width="100%"
            height="100%"
            cursor={onClick ? "pointer" : null}
            onClick={onClick ? handleClick : null}
          >
            <Image
              src={src}
              fallback={<></>}
              width="100%"
              height="100%"
              objectFit="cover"
            />
          </Box>
        </Tooltip>
        {type === PostMediaTypes.VIDEO ? (
          <Box sx={timeStyles}>{formatDuration(duration || 0)}</Box>
        ) : null}
        {inProgress ? (
          <Box sx={progressStyles}>
            <CircularProgress
              capIsRound
              value={progress}
              isIndeterminate={progress === 0 || progress === 100}
              size={size === "sm" ? "36px" : "56px"}
              lineHeight="1"
              thickness="6px"
              trackColor="gray.600"
              color="primary.400"
            >
              <CircularProgressLabel>
                {progress === 0 || progress === 100 ? "" : `${progress}%`}
              </CircularProgressLabel>
            </CircularProgress>
          </Box>
        ) : null}
        {errors?.length ? (
          <Box sx={errorStyles}>
            <Tooltip
              label={errors?.map((item) => (
                <Box>{item}</Box>
              ))}
              hasArrow
              placement="top"
              variant="error"
              shouldWrapChildren
            >
              <Icon
                as={FaExclamationTriangle}
                fontSize={size === "sm" ? "md" : "xl"}
              />
            </Tooltip>
          </Box>
        ) : null}
      </Box>
      {(isSmallScreen || hover) && onRemove ? (
        <Tooltip
          label={removeTooltip || t("post_media_list.actions.remove")}
          hasArrow
          placement="top"
        >
          <IconButton
            icon={<Icon as={FaTimes} />}
            onClick={handleRemove}
            size={toolbarButtonSize}
            sx={{
              ...toolbarButtonStyles,
              position: "absolute",
              top: "1",
              right: "1",
            }}
          />
        </Tooltip>
      ) : null}
      {(isSmallScreen || hover) && onMove && !(isFirst && isLast) ? (
        <Tooltip
          label={t("post_media_list.actions.move_left")}
          hasArrow
          placement="top"
        >
          <IconButton
            icon={<Icon as={FaChevronLeft} />}
            onClick={handleMoveLeft}
            size={toolbarButtonSize}
            sx={{
              ...toolbarButtonStyles,
              position: "absolute",
              top: "50%",
              left: "1",
              transform: "translateY(-50%)",
            }}
            isDisabled={isFirst}
          />
        </Tooltip>
      ) : null}
      {(isSmallScreen || hover) && onMove && !(isFirst && isLast) ? (
        <Tooltip
          label={t("post_media_list.actions.move_right")}
          hasArrow
          placement="top"
        >
          <IconButton
            icon={<Icon as={FaChevronRight} />}
            onClick={handleMoveRight}
            size={toolbarButtonSize}
            sx={{
              ...toolbarButtonStyles,
              position: "absolute",
              top: "50%",
              right: "1",
              transform: "translateY(-50%)",
            }}
            isDisabled={isLast}
          />
        </Tooltip>
      ) : null}
      {(isSmallScreen || hover) && onEditThumb ? (
        <Tooltip
          label={t("post_media_list.actions.change_thumbnail")}
          hasArrow
          placement="bottom"
        >
          <Button
            leftIcon={<Icon as={FaPencilAlt} />}
            onClick={handleEditThumb}
            size={toolbarButtonSize}
            sx={{
              ...toolbarButtonStyles,
              position: "absolute",
              bottom: "1",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            {t("post_media_list.actions.thumbnail")}
          </Button>
        </Tooltip>
      ) : null}
      {(isSmallScreen || hover) && onAdd ? (
        <Tooltip
          label={t("post_media_list.actions.add_to_post")}
          hasArrow
          placement="bottom"
        >
          <Button
            leftIcon={<Icon as={FaPlus} />}
            onClick={handleAdd}
            size={toolbarButtonSize}
            sx={{
              ...toolbarButtonStyles,
              position: "absolute",
              bottom: "1",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            {t("post_media_list.actions.add")}
          </Button>
        </Tooltip>
      ) : null}
    </Box>
  );
};

const TooltipPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.node,
  PropTypes.func,
  PropTypes.arrayOf(PropTypes.node),
]);

PostMediaListItem.propTypes = {
  id: PropTypes.string,
  type: PropTypes.oneOf([PostMediaTypes.PHOTO, PostMediaTypes.VIDEO])
    .isRequired,
  src: PropTypes.string,
  progress: PropTypes.number,
  duration: PropTypes.number,
  errors: PropTypes.arrayOf(PropTypes.string),
  onClick: PropTypes.func,
  onAdd: PropTypes.func,
  onEditThumb: PropTypes.func,
  onRemove: PropTypes.func,
  onMove: PropTypes.func,
  clickTooltip: TooltipPropType,
  removeTooltip: TooltipPropType,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  size: PropTypes.oneOf(["md", "sm"]),
};

PostMediaListItem.defaultProps = {
  id: undefined,
  src: undefined,
  progress: undefined,
  duration: undefined,
  errors: undefined,
  onClick: undefined,
  onAdd: undefined,
  onEditThumb: undefined,
  onRemove: undefined,
  onMove: undefined,
  clickTooltip: undefined,
  removeTooltip: undefined,
  isFirst: undefined,
  isLast: undefined,
  size: "md",
};

export default memo(PostMediaListItem);
